import { createFileRoute, redirect, useNavigate, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { z } from "zod";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/Logo";
import {
  checkLoginAllowed,
  recordLoginFailure,
  clearLoginAttempts,
} from "@/lib/login-guard.functions";
import { beginInternalLogin } from "@/lib/mfa.functions";
import {
  handleInternalLoginFlow,
  type InternalLoginFlow,
} from "@/lib/internal-login";

import { creategyEmailSchema, isCreategyEmail } from "@/lib/creategy-domain";
import {
  TurnstileWidget,
  type TurnstileHandle,
} from "@/components/security/TurnstileWidget";
import { requestAccessLink } from "@/lib/access-link.functions";
import { Button } from "@/components/ui/button";
import { LoaderCircle } from "lucide-react";
import {
  detectAuthCallback,
  buildResetPasswordTarget,
} from "@/lib/intern-auth-callback";


const loginSchema = z.object({
  email: creategyEmailSchema,
  password: z.string().min(6, "Mindestens 6 Zeichen").max(72),
});

const GENERIC_ERROR = "Anmeldung fehlgeschlagen. Bitte E-Mail und Passwort prüfen.";

function formatLockMessage(lockedUntilIso: string): string {
  const remainingMs = new Date(lockedUntilIso).getTime() - Date.now();
  const minutes = Math.max(1, Math.ceil(remainingMs / 60_000));
  return `Zu viele Fehlversuche. Bitte in ca. ${minutes} Minute${minutes === 1 ? "" : "n"} erneut versuchen.`;
}


export const Route = createFileRoute("/intern")({
  ssr: false,
  pendingMs: 100,
  pendingMinMs: 300,
  head: () => ({
    meta: [
      { title: "Login – creategy" },
      { name: "description", content: "Geschützte Anmeldung für das creategy-Team." },
      { property: "og:title", content: "Login – creategy" },
      { property: "og:description", content: "Geschützte Anmeldung für das creategy-Team." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : "/admin",
  }),
  beforeLoad: async ({ search }) => {
    if (typeof window !== "undefined") {
      const cb = detectAuthCallback();
      if (cb) {
        throw redirect({ href: buildResetPasswordTarget(cb), replace: true });
      }
    }
    // Dynamischer Import: supabase-js soll nicht im Entry-Bundle landen.
    const { supabase: sb } = await import("@/integrations/supabase/client");
    const { data } = await sb.auth.getUser();
    if (data.user) {
      const email = (data.user.email ?? "").toLowerCase();
      // Externe Konten (Kunden) gehören ins Cockpit, nicht in den internen Bereich.
      if (email && !email.endsWith("@creategy.de")) {
        throw redirect({ to: "/cockpit" });
      }
      // Invite noch nicht abgeschlossen? → auf Passwort-Setup lenken, niemals ins Backend.
      const meta = (data.user.user_metadata ?? {}) as Record<string, unknown>;
      const { isOnboardingComplete } = await import("@/lib/onboarding-status");
      const onboardingComplete = isOnboardingComplete({
        passwordSetAt:
          typeof meta.password_set_at === "string" ? meta.password_set_at : null,
        invitedAt: typeof meta.invited_at === "string" ? meta.invited_at : null,
        lastSignInAt: data.user.last_sign_in_at ?? null,
      });
      if (!onboardingComplete) {
        throw redirect({
          href: "/intern/reset-password?flow=invite",
          replace: true,
        });
      }
      throw redirect({ to: search.redirect });
    }
  },
  pendingComponent: InternalLoginPending,
  errorComponent: InternalLoginError,
  component: LoginPage,
});

function InternalLoginPending() {
  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-6 md:px-12">
          <Logo />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="flex items-center gap-3 text-sm text-muted-foreground" role="status">
          <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden />
          Anmeldung wird geprüft …
        </div>
      </main>
    </div>
  );
}

function InternalLoginError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-6 md:px-12">
          <Logo />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-md text-center">
          <h1 className="font-display text-3xl font-extrabold">Anmeldung nicht erreichbar</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Der Anmeldestatus konnte gerade nicht geladen werden. Ihre Sitzung bleibt dabei unverändert.
          </p>
          <Button
            className="mt-6"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Erneut versuchen
          </Button>
        </div>
      </main>
    </div>
  );
}

function LoginPage() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const beginLogin = useServerFn(beginInternalLogin);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const trimmedEmail = email.trim();
  const domainInvalid =
    trimmedEmail.includes("@") && !isCreategyEmail(trimmedEmail);

  // Fallback: falls beforeLoad (SSR) den Callback nicht abgefangen hat,
  // erkennen wir Invite-/Recovery-Tokens hier im Client und leiten sofort weiter.
  useEffect(() => {
    const cb = detectAuthCallback();
    if (!cb) return;
    window.location.replace(buildResetPasswordTarget(cb));
  }, []);

  const resetTurnstile = () => {
    turnstileRef.current?.reset();
    setTurnstileToken(null);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const parsed = loginSchema.safeParse({ email, password });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? "Eingabe ungültig");
      return;
    }
    if (!turnstileToken) {
      setError("Bitte Bot-Schutz-Prüfung abschließen.");
      return;
    }
    setBusy(true);
    try {
      const normalizedEmail = parsed.data.email.toLowerCase();

      const gate = await checkLoginAllowed({
        data: { email: normalizedEmail, turnstileToken },
      });
      // Der Token ist nach dem Server-Verify verbraucht → sofort neu einholen.
      resetTurnstile();
      if (!gate.allowed) {
        if (gate.reason === "turnstile") {
          setError(
            "Bot-Schutz konnte nicht bestätigt werden. Bitte erneut versuchen.",
          );
        } else if (gate.lockedUntil) {
          setError(formatLockMessage(gate.lockedUntil));
        } else {
          setError(GENERIC_ERROR);
        }
        return;
      }

      const { error: authErr } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password: parsed.data.password,
      });

      if (authErr) {
        const failure = await recordLoginFailure({
          data: { email: normalizedEmail },
        }).catch(() => null);
        if (failure?.lockedUntil) {
          setError(formatLockMessage(failure.lockedUntil));
        } else {
          setError(GENERIC_ERROR);
        }
        return;
      }

      await clearLoginAttempts({ data: { email: normalizedEmail } }).catch(() => {});

      // 2FA-Weiche: Für beide internen Rollen ist ein Code Pflicht.
      // Ticket V-SK1: Ein Konto ohne interne Rolle wird nicht mehr still
      // weitergeleitet, sondern erhält eine neutrale Meldung – und die
      // Sitzung wird beendet, damit keine angemeldete Hülle zurückbleibt.
      try {
        const flow = await beginLogin();
        await handleInternalLoginFlow(flow as InternalLoginFlow, {
          signOut: async () => {
            await supabase.auth.signOut();
          },
          showError: setError,
          goToMfa: () =>
            navigate({
              to: "/intern/mfa",
              search: { redirect: search.redirect ?? "/admin" },
              replace: true,
            }),
        });
        return;

      } catch (err) {
        console.error("beginInternalLogin failed", err);
        // Bei einem Fehler in der Weiche bleiben wir auf Nummer sicher und
        // schicken den Nutzer zur Codeeingabe. Der Admin-Gate lässt keine
        // ungeprüften Sessions ins Backend.
        navigate({
          to: "/intern/mfa",
          search: { redirect: search.redirect ?? "/admin" },
          replace: true,
        });
        return;
      }


      navigate({ to: search.redirect, replace: true });
    } finally {
      setBusy(false);
    }
  };


  const onReset = async () => {
    setError(null);
    const parsed = z.string().email().safeParse(email.trim());
    if (!parsed.success) {
      setError("Bitte zuerst eine gültige E-Mail-Adresse oben eintragen.");
      return;
    }
    const normalized = parsed.data.toLowerCase();
    // Serverseitig anfordern: dort wird zwischen Einladungs- und Reset-Mail
    // unterschieden und der Fehlerfall protokolliert.
    try {
      const outcome = await requestAccessLink({
        data: { email: normalized, area: "intern", origin: window.location.origin },
      });
      setError(outcome.message);
    } catch {
      setError(
        "Konnte E-Mail nicht senden. Bitte versuchen Sie es in wenigen Minuten erneut.",
      );
    }
  };


  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border">
        <div className="mx-auto flex h-16 max-w-6xl items-center px-6 md:px-12">
          <Logo />
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <h1 className="font-display text-3xl font-extrabold">Anmelden</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Zugang ausschließlich für autorisierte Mitarbeitende der creategy GmbH.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-semibold">
                E-Mail
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={domainInvalid || undefined}
                aria-describedby="email-domain-hint"
                className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
              />
              <p
                id="email-domain-hint"
                role={domainInvalid ? "alert" : undefined}
                className={
                  "mt-1.5 text-xs " +
                  (domainInvalid ? "text-destructive" : "text-muted-foreground")
                }
              >
                {domainInvalid
                  ? "Diese E-Mail-Adresse gehört nicht zu creategy. Bitte verwenden Sie Ihre @creategy.de-Adresse."
                  : "Nur E-Mail-Adressen mit @creategy.de sind zugelassen."}
              </p>
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-semibold">
                Passwort
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-foreground"
              />
            </div>

            <div className="space-y-2">
              <TurnstileWidget action="login"
                ref={turnstileRef}
                context="login"
                onToken={(token) => {
                  setTurnstileToken(token);
                  if (token) setTurnstileError(null);
                }}
                onError={(message) => setTurnstileError(message)}
              />
              {turnstileError && (
                <p role="alert" className="text-xs text-destructive">
                  {turnstileError}
                </p>
              )}
            </div>

            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy || domainInvalid || !turnstileToken}
              className="w-full rounded-md bg-brand-button px-4 py-2.5 text-sm font-semibold text-brand-button-foreground transition-opacity hover:opacity-90 disabled:opacity-60"
            >
              {busy ? "Einen Moment…" : "Anmelden"}
            </button>

            <button
              type="button"
              onClick={onReset}
              className="block w-full text-left text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
            >
              Passwort vergessen?
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}